import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Resources = () => {
  const navigate = useNavigate();
  const [expandedCategory, setExpandedCategory] = useState('scholarships');

  const resourceCategories = [
    {
      id: 'scholarships',
      title: 'Scholarships in China',
      icon: 'Award',
      description: 'Complete guides and white papers about Chinese scholarships',
      resources: [
        {
          name: 'Chinese Government Scholarships (CSC)',
          description: 'Full information about CSC scholarships, eligibility criteria, and application process',
          type: 'White Paper',
          downloadUrl: '#',
        },
        {
          name: 'University-Specific Scholarships',
          description: 'Scholarships offered by top Chinese universities',
          type: 'Guide',
          downloadUrl: '#',
        },
        {
          name: 'Scholarship Application Checklist',
          description: 'Complete checklist of required documents for scholarship applications',
          type: 'Checklist',
          downloadUrl: '#',
        },
        {
          name: 'Scholarship Timeline & Deadlines',
          description: 'Important dates and application deadlines for major scholarships',
          type: 'Reference',
          downloadUrl: '#',
        },
      ],
    },
    {
      id: 'financial',
      title: 'Financial Guide for International Students',
      icon: 'DollarSign',
      description: 'Complete financial planning guide before coming to China',
      resources: [
        {
          name: 'Cost of Living in Major Chinese Cities',
          description: 'Detailed breakdown of expenses in Beijing, Shanghai, Guangzhou, and more',
          type: 'Guide',
          downloadUrl: '#',
        },
        {
          name: 'Banking & Money Transfer Guide',
          description: 'How to open a bank account, transfer money, and manage finances in China',
          type: 'Guide',
          downloadUrl: '#',
        },
        {
          name: 'Student Budget Planner',
          description: 'Interactive tool to plan your monthly budget as an international student',
          type: 'Tool',
          downloadUrl: '#',
        },
        {
          name: 'Part-Time Work Regulations',
          description: 'Legal work hours, taxation, and employment regulations for international students',
          type: 'White Paper',
          downloadUrl: '#',
        },
      ],
    },
    {
      id: 'tips',
      title: 'Chinese Language & Cultural Tips',
      icon: 'Lightbulb',
      description: 'Practical tips for learning Chinese and adapting to Chinese culture',
      resources: [
        {
          name: 'Common Chinese Phrases for Daily Life',
          description: 'Essential phrases for shopping, eating, transportation, and social interactions',
          type: 'Quick Guide',
          downloadUrl: '#',
        },
        {
          name: 'Chinese Etiquette & Social Customs',
          description: 'Understanding Chinese social norms, business etiquette, and cultural practices',
          type: 'Guide',
          downloadUrl: '#',
        },
        {
          name: 'Learning Chinese Efficiently',
          description: 'Tips and strategies for accelerating your Chinese language learning',
          type: 'Tips & Tricks',
          downloadUrl: '#',
        },
        {
          name: 'Chinese Festivals & Celebrations',
          description: 'Guide to major Chinese festivals and how they\'re celebrated',
          type: 'Cultural Guide',
          downloadUrl: '#',
        },
      ],
    },
    {
      id: 'universities',
      title: 'Guide to Choosing a University',
      icon: 'BookOpen',
      description: 'Comprehensive guide on selecting the right university in China',
      resources: [
        {
          name: 'Top Universities in China - Ranking & Overview',
          description: 'Rankings, specialties, and profiles of China\'s leading universities',
          type: 'Ranking Guide',
          downloadUrl: '#',
        },
        {
          name: 'University Comparison Tool',
          description: 'Compare programs, fees, locations, and facilities across universities',
          type: 'Interactive Tool',
          downloadUrl: '#',
        },
        {
          name: 'Admission Requirements by University',
          description: 'Detailed admission criteria for international students at major universities',
          type: 'Reference',
          downloadUrl: '#',
        },
        {
          name: 'Campus Life & Student Services',
          description: 'Information about student accommodations, facilities, and support services',
          type: 'Guide',
          downloadUrl: '#',
        },
      ],
    },
    {
      id: 'cities',
      title: 'Introduction to Major Chinese Cities',
      icon: 'MapPin',
      description: 'Comprehensive guides to living in China\'s major cities',
      resources: [
        {
          name: 'Beijing - Capital City Guide',
          description: 'History, culture, transportation, attractions, and student life in Beijing',
          type: 'City Guide',
          downloadUrl: '#',
        },
        {
          name: 'Shanghai - Modern China Guide',
          description: 'Explore China\'s financial hub with insider tips and practical information',
          type: 'City Guide',
          downloadUrl: '#',
        },
        {
          name: 'Xi\'an, Chengdu, Guangzhou & More',
          description: 'Guides to other major cities offering excellent study opportunities',
          type: 'City Guides',
          downloadUrl: '#',
        },
        {
          name: 'Transportation Guide Across China',
          description: 'How to navigate transportation networks between cities',
          type: 'Travel Guide',
          downloadUrl: '#',
        },
      ],
    },
    {
      id: 'documents',
      title: 'Required Documents & Application Procedures',
      icon: 'FileText',
      description: 'Checklist and guide for visa and university applications',
      resources: [
        {
          name: 'Student Visa (X Visa) Requirements',
          description: 'Complete checklist of documents needed for a student visa application',
          type: 'Checklist',
          downloadUrl: '#',
        },
        {
          name: 'University Application Documents',
          description: 'Required documents including transcripts, letters of recommendation, and essays',
          type: 'Checklist',
          downloadUrl: '#',
        },
        {
          name: 'Document Apostille & Notarization',
          description: 'Guide on how to authenticate and translate official documents',
          type: 'How-To Guide',
          downloadUrl: '#',
        },
        {
          name: 'Application Timeline & Deadlines',
          description: 'Step-by-step timeline for preparing and submitting applications',
          type: 'Timeline',
          downloadUrl: '#',
        },
      ],
    },
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-primary/5 border-b border-border">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Icon name="BookOpen" size={40} color="var(--color-primary)" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Resources & Guides
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive guides, white papers, and practical information to help you navigate your journey in Chinese education and culture.
          </p>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-6">
            {resourceCategories.map((category) => (
              <div
                key={category.id}
                className="border border-border rounded-lg overflow-hidden bg-card hover:shadow-warm transition-smooth"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-6 py-6 flex items-start justify-between hover:bg-muted/30 transition-smooth"
                >
                  <div className="flex items-start gap-4 text-left">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon name={category.icon} size={24} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold text-foreground">
                        {category.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <Icon
                    name={expandedCategory === category.id ? 'ChevronUp' : 'ChevronDown'}
                    size={24}
                    color="var(--color-muted-foreground)"
                    className="flex-shrink-0"
                  />
                </button>

                {/* Category Content */}
                {expandedCategory === category.id && (
                  <div className="px-6 py-6 border-t border-border bg-muted/5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {category.resources.map((resource, index) => (
                        <div
                          key={index}
                          className="p-4 border border-border rounded-lg bg-background hover:shadow-warm transition-smooth"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h4 className="text-sm font-heading font-semibold text-foreground">
                              {resource.name}
                            </h4>
                            <span className="inline-block px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded">
                              {resource.type}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-4">
                            {resource.description}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            iconPosition="right"
                            onClick={() => {
                              // Handle download logic
                            }}
                          >
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 p-8 bg-primary/5 border border-border rounded-lg text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
              Need More Help?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Can't find what you're looking for? Contact our support team or browse our frequently asked questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                iconName="Mail"
                iconPosition="right"
                onClick={() => {}}
              >
                Contact Us
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/institution-search')}
              >
                Browse Programs
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;
